import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_status: string | null;
  subscription_end: string | null;
}

interface SubscriptionContextType {
  subscription: SubscriptionData | null;
  isLoading: boolean;
  refreshSubscription: () => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, session } = useAuth();

  const refreshSubscription = async () => {
    if (!user) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        }
      });

      if (error) {
        console.error('❌ Error checking subscription:', error);
        
        // Gérer l'expiration de session sans redirection brutale
        if (error.message?.includes('session_expired') || error.message?.includes('Session')) {
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            await supabase.auth.signOut();
            // Le onAuthStateChange dans useAuth gèrera la redirection
            return;
          }
          // Retry after refresh
          return refreshSubscription();
        }
        throw error;
      }

      // Gérer le code d'erreur dans les données
      if (data?.code === 'session_expired') {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          await supabase.auth.signOut();
          return;
        }
        return refreshSubscription();
      }

      setSubscription(data);
      
    } catch (error) {
      console.error('❌ Failed to check subscription:', error);
      setSubscription({
        subscribed: false,
        subscription_tier: null,
        subscription_status: null,
        subscription_end: null
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    if (!user || !session) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour accéder au portail client",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ouverture du portail client:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ouvrir le portail client",
        variant: "destructive"
      });
    }
  };

  // Check subscription when user changes
  useEffect(() => {
    refreshSubscription();
  }, [user]);

  // Removed automatic subscription refresh - will only refresh on user change

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      isLoading,
      refreshSubscription,
      openCustomerPortal
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}